/**
 * CSV Export Utilities for Chat V2
 * Helper functions for downloading complete datasets from workflow events
 */

import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from './onboardingLocalStorage';

export interface DataSourceV2 {
  event_id: string;
  source_type: 'compute' | 'read';
  frame_id: string;
  description: string;
  row_count: number | null;
  column_count: number | null;
  connector_name?: string;
  entity_name?: string;
  is_default: boolean;
}

export interface DataExportOptionsV2 {
  has_data: boolean;
  options: DataSourceV2[];
  default_frame_id: string | null;
}

/**
 * Check if a workflow has exportable data
 */
export async function checkWorkflowHasData(workflowId: string): Promise<boolean> {
  try {
    const token = localStorage.getItem('klaris_jwt');
    const tid = getTenantIdFromStorage();
    if (!token || !tid) return false;

    const url = buildApiUrl(`${tenantApiPrefix(tid)}/chat-v2/workflows/${workflowId}/data/options`);
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) return false;

    const data: DataExportOptionsV2 = await response.json();
    return data.has_data;
  } catch {
    return false;
  }
}

/**
 * Get available data export options for a workflow
 */
export async function getWorkflowDataExportOptions(workflowId: string): Promise<DataExportOptionsV2 | null> {
  try {
    const token = localStorage.getItem('klaris_jwt');
    const tid = getTenantIdFromStorage();
    if (!token || !tid) {
      throw new Error('Not authenticated');
    }

    const url = buildApiUrl(`${tenantApiPrefix(tid)}/chat-v2/workflows/${workflowId}/data/options`);
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to get export options');
    }

    return await response.json();
  } catch (error: any) {
    console.error('Error getting export options:', error);
    throw error;
  }
}

/**
 * Download workflow data as CSV
 */
export async function downloadWorkflowDataCSV(
  workflowId: string,
  frameId?: string,
  format: 'csv' | 'tsv' = 'csv'
): Promise<{ filename: string; rowCount: number | null; dataSource: string | null }> {
  try {
    const token = localStorage.getItem('klaris_jwt');
    const tid = getTenantIdFromStorage();
    if (!token || !tid) {
      throw new Error('Not authenticated');
    }

    // Build URL with optional frame_id
    const params = new URLSearchParams({ format });
    if (frameId) {
      params.append('frame_id', frameId);
    }

    const url = buildApiUrl(`${tenantApiPrefix(tid)}/chat-v2/workflows/${workflowId}/data/csv?${params.toString()}`);

    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Failed to export data');
    }

    // Get filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `data-${workflowId.slice(0, 8)}.${format}`;
    
    if (contentDisposition) {
      const matches = contentDisposition.match(/filename=["']?([^"';]+)["']?/);
      if (matches && matches[1]) {
        filename = matches[1];
      }
    }

    // Get metadata from headers
    const rowCount = response.headers.get('X-Row-Count');
    const dataSource = response.headers.get('X-Data-Source');

    // Download file
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    // Return metadata for success message
    return {
      filename,
      rowCount: rowCount ? parseInt(rowCount) : null,
      dataSource,
    };
  } catch (error: any) {
    console.error('Error downloading CSV:', error);
    throw error;
  }
}

/**
 * Format row count for display
 */
export function formatRowCount(count: number | null): string {
  if (count === null) return '';
  if (count < 1000) return `${count} rows`;
  if (count < 1000000) return `${(count / 1000).toFixed(1)}K rows`;
  return `${(count / 1000000).toFixed(1)}M rows`;
}

/**
 * Get friendly description for source type
 */
export function getSourceTypeLabel(sourceType: string): string {
  switch (sourceType) {
    case 'compute':
      return 'Computed Result';
    case 'read':
      return 'Raw Data';
    default:
      return sourceType;
  }
}
